<template>
  <div>
    <!--
      Dropdown menu wrapping the agent status button. Items are built from
      dropMenuItems so only the contextually relevant action is enabled.

      The Button is BOTH the dropdown trigger and the tooltip trigger: the two
      `as-child` triggers are nested directly around the single <Button> so the
      click/hover handlers merge onto the same real element. (TooltipProvider is
      renderless, so it must sit OUTSIDE the triggers — never as their as-child.)

      GOTCHA: reka-ui resolves each Popper's anchor via the *nearest* PopperRoot
      (plain provide/inject, no scoping). Because <Tooltip> is itself a PopperRoot
      wrapping the trigger, the dropdown's internal MenuAnchor would bind to the
      tooltip's popper instead of the dropdown's, leaving DropdownMenuContent with
      no anchor to position against (it stays invisible — "click does nothing").
      Fix: hand DropdownMenuContent an explicit `:reference` (the trigger element,
      read off TooltipTrigger's exposed $el) so it positions independently of the
      hijacked anchor.
    -->
    <!--
      Button look matches the topbar search field; the tint carries the agent status.
      Keep comments OUT of the as-child trigger slots: in dev builds Vue keeps them, the slot then
      renders a fragment (comment + button) and the triggers lose their element.
    -->
    <TooltipProvider v-if="botHasLivechat && agent">
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger as-child>
            <DropdownMenuTrigger as-child>
              <Button
                ref="triggerRef"
                :aria-label="t(`livechat.headerBtn.${agentStatus}`)"
                variant="ghost"
                size="icon"
                class="livechat-header-btn relative size-9 rounded-lg border"
                :class="{
                  'livechat-header-btn--other-tab border-amber-300/40 bg-amber-300/10 text-amber-200 hover:bg-amber-300/20 hover:text-amber-100':
                    agentStatus === statusTypes.otherTab,
                  'border-emerald-300/40 bg-emerald-400/15 text-emerald-200 hover:bg-emerald-400/25 hover:text-emerald-100':
                    agentStatus === statusTypes.online,
                  'border-white/15 bg-white/5 text-white/70 hover:border-white/30 hover:bg-white/10 hover:text-white':
                    agentStatus === statusTypes.offline,
                }"
              >
                <!-- Bell-off: livechat is active in another browser tab -->
                <BellOff
                  v-if="agentStatus === statusTypes.otherTab"
                  :size="18"
                />
                <!-- Headset: agent is online -->
                <Headset
                  v-if="agentStatus === statusTypes.online"
                  :size="18"
                />
                <!-- HeadphoneOff: agent is offline -->
                <HeadphoneOff
                  v-if="agentStatus === statusTypes.offline"
                  :size="18"
                />
                <!-- Pulse indicator when there are pending incoming chats/messages -->
                <PulseBoeble v-if="hasNotifications" class="livechat-header-btn-pulse" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>

          <TooltipContent>
            <span>{{ t(`livechat.headerBtn.${agentStatus}`) }}</span>
          </TooltipContent>
        </Tooltip>

        <DropdownMenuContent align="center" :reference="triggerEl">

          <!-- Agent is online; go offline -->
          <div v-if="agentStatus === statusTypes.online" class="flex flex-col items-center p-2">
            <span>Online</span>
            <p class="text-xs text-muted-foreground mb-2">
              You are online in the livechat
            </p>
            <Button
              size="sm"
              variant="destructive"
              class="w-full"
              @click="changeStatus(false)"
            >
              Go Offline
              <HeadphoneOff />
            </Button>
          </div>
          <!-- Not online at all; go online -->
          <div v-else-if="agentStatus === statusTypes.offline" class="flex flex-col items-center p-2">
            <span>Not Online</span>
            <p class="text-sm text-muted-foreground mb-2">
              You are not online in the livechat
            </p>
            <Button
              size="sm"
              variant="secondary"
              class="w-full"
              @click="changeStatus(true)"
            >
              Go Online
              <Headset />
            </Button>
          </div>
          <!-- Online in other tab; go here instead -->
          <div v-else-if="agentStatus === statusTypes.otherTab" class="flex flex-col items-center p-2">
            <span>Using Other Tab</span>
            <p class="text-xs text-muted-foreground mb-2">
              Another tab is used for livechat
            </p>
            <Button
              size="sm"
              variant="secondary"
              class="w-full"
              @click="changeStatus(true)"
            >
              Use this tab
              <Headset />
            </Button>
          </div>

          <template v-if="false">
            <DropdownMenuItem
              v-for="item in dropMenuItems"
              :key="item.text"
              :disabled="item.isDisabled"
              @click="item.onClick"
            >
              <component :is="item.icon" :size="16" class="mr-2" />
              {{ item.text }}
            </DropdownMenuItem>
          </template>
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>

    <!--
      Inactivity modal: shown when the agent has been inactive too long.
      Counts down `inactiveTimeoutLeft` seconds; on expiry the agent is
      automatically set offline and all open chats are closed.
    -->
    <HubDialog
      v-model="isAgentInactiveOpen"
      :title="t('livechat.agentInactiveModal.title')"
      :primaryLabel="t('livechat.agentInactiveModal.btnStay')"
      modal
      @onPrimary="onAgentIsActive"
      @onSecondary="onAgentIsInactive"
    >
      <div class="text-center">
        <h2 class="mt-4 text-2xl font-bold">{{ inactiveTimeoutLeft }}</h2>
        <p class="mt-2">{{ t('livechat.agentInactiveModal.countdown') }}</p>
      </div>
    </HubDialog>
  </div>
</template>

<script setup lang="ts">
import { unrefElement } from "@vueuse/core";
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  BellOff,
  Headset,
  HeadphoneOff,
  BellRing,
} from '@lucide/vue';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import HubDialog from '@/components/hub/HubDialog.vue';
import PulseBoeble from '@/components/common/PulseBoeble.vue';
import { statusTypes } from '@/utils/livechat';
import { useLivechatStore } from '@/stores/livechat';
import { useInactivityTrackerStore } from '@/stores/inactivityTracker';

const livechatStore = useLivechatStore();
const inactivityTrackerStore = useInactivityTrackerStore();
const { t } = useI18n();

/**
 * TooltipTrigger exposes the resolved trigger DOM element as `$el`. We feed it to
 * DropdownMenuContent's `reference` so the menu positions against the button
 * directly, sidestepping reka-ui's nearest-PopperRoot anchor collision (see the
 * template comment above the dropdown).
 */
// The ref sits on the Button itself rather than reading TooltipTrigger's $el: when the as-child
// slot renders more than one node, $el is a fragment placeholder text node that floating-ui
// can't measure (the menu then stays parked off-screen). unrefElement resolves the real <button>.
const triggerRef = ref<any>(null);
const triggerEl = computed<HTMLElement | undefined>(() => {
  const el = unrefElement(triggerRef);
  return el instanceof HTMLElement ? el : undefined;
});

// ── Store getters ──────────────────────────────────────────────────────────────
const botHasLivechat = computed(() => livechatStore.botHasLivechatGetter);
const agent = computed(() => livechatStore.agentGetter);
const agentStatus = computed(() => livechatStore.agentStatus);
const isAgentInactive = computed(() => livechatStore.isAgentInactiveGetter);
const incomeType = computed(() => livechatStore.incomeType);

/** True when there is an unacknowledged incoming chat or message */
const hasNotifications = computed(
  () => incomeType.value === 'newChat' || incomeType.value === 'newMessage',
);

// ── Inactivity countdown ───────────────────────────────────────────────────────
/** Duration of the countdown before the agent is auto-logged-off (seconds) */
const INACTIVE_TIMEOUT_LIMIT = 60;

const inactiveTimestamp = ref(0);
const inactiveTimeoutLeft = ref(INACTIVE_TIMEOUT_LIMIT);
const inactiveTimeInterval = ref<ReturnType<typeof setInterval> | null>(null);

/**
 * Proxy ref so the dialog v-model follows the store getter but we avoid
 * directly binding a computed to v-model.
 */
const isAgentInactiveOpen = computed(() => isAgentInactive.value);

watch(isAgentInactive, (inactive) => {
  if (inactive) {
    // Record the timestamp when the countdown will hit zero
    inactiveTimestamp.value = Date.now() + INACTIVE_TIMEOUT_LIMIT * 1000;
    inactiveTimeInterval.value = setInterval(() => {
      inactiveTimeoutLeft.value = Math.floor(
        (inactiveTimestamp.value - Date.now()) / 1000,
      );
      if (inactiveTimeoutLeft.value <= 0) {
        inactiveTimeoutLeft.value = 0;
        clearInterval(inactiveTimeInterval.value!);
        onAgentIsInactive();
      }
    }, 1000);
  } else {
    // Reset on dismiss / "I'm still here"
    inactiveTimeoutLeft.value = INACTIVE_TIMEOUT_LIMIT;
    inactiveTimestamp.value = 0;
    if (inactiveTimeInterval.value) {
      clearInterval(inactiveTimeInterval.value);
      inactiveTimeInterval.value = null;
    }
  }
});

// Clean up interval when component is destroyed
onBeforeUnmount(() => {
  if (inactiveTimeInterval.value) clearInterval(inactiveTimeInterval.value);
});

// ── Agent status actions ───────────────────────────────────────────────────────
function changeStatus(active: boolean) {
  if (active) {
    livechatStore.goOnline();
  } else {
    livechatStore.goOffline();
  }
}

function onAgentIsActive() {
  inactivityTrackerStore.restartTimeoutTimer();
  livechatStore.resetAgentInactive();
}

async function onAgentIsInactive() {
  await livechatStore.closeAllOpenChats();
  livechatStore.goOffline();
}

// ── Dropdown menu items ────────────────────────────────────────────────────────
const dropMenuItems = computed(() => [
  {
    text: t('livechat.headerBtn.btnGoOnline'),
    icon: Headset,
    onClick: () => changeStatus(true),
    isDisabled: agentStatus.value !== statusTypes.offline,
  },
  {
    text: t('livechat.headerBtn.btnGoOffline'),
    icon: HeadphoneOff,
    onClick: () => changeStatus(false),
    isDisabled: agentStatus.value !== statusTypes.online,
  },
  {
    text: t('livechat.headerBtn.btnTabActive'),
    icon: BellRing,
    onClick: () => changeStatus(true),
    isDisabled: agentStatus.value !== statusTypes.otherTab,
  },
]);
</script>

<style scoped>
/** Position the pulse indicator in the top-right corner of the button */
.livechat-header-btn-pulse {
  position: absolute;
  top: 0;
  right: 0;
}
</style>
